import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Leuprolide — the same vial doing two different jobs.
 *
 * At 0.5–1 mg daily it suppresses the pituitary for weeks. At 1–4 mg once it
 * triggers final maturation, 34 to 36 hours before retrieval. At 20–40 mcg
 * twice a day it is a micro-dose flare. Nothing in a prescription record says
 * which of those she is doing, and the procedure is the same either way, so
 * this protocol describes the act and never the intent.
 *
 * That is why its `indication` is `agonist` rather than `trigger`. A patient on
 * daily down-regulation must not meet this walk on her trigger-shot step. Where
 * leuprolide *is* the trigger — the agonist half of a dual trigger — she
 * reaches it from the hCG walk her clinic actually timed.
 *
 * ### The one number this procedure turns on
 *
 * Volume. Leuprolide is given in volumes small enough that the syringe markings
 * are the dose: the content says "read the syringe markings precisely", and for
 * a micro-dose protocol the clinic may have her dilute it first, which changes
 * what every marking means. Those two sentences are on the drawing step, and
 * that step asks her to confirm — it is the only place in this walk where being
 * wrong is silent.
 */

const LUPRON_BRANDS = [
  { name: "Lupron", markets: ["US", "CA", "BR"] },
  { name: "Prostap", markets: ["GB", "IE"] },
  {
    name: "Lucrin",
    markets: ["AU", "NZ", "CH", "SG", "MY", "VN", "NL", "MX"],
  },
  { name: "Procrin", markets: ["ES", "MX"] },
  { name: "Procren", markets: ["FI", "NO", "SE"] },
  { name: "Enantone", markets: ["DE", "FR", "IT", "AT", "HK", "TH"] },
  { name: "Leuplin", markets: ["JP"] },
  { name: "Luprolex", markets: ["PH"] },
  { name: "Tapros", markets: ["ID"] },
] as const satisfies readonly ProtocolBrand[];

/** The same two hours the antagonists draw, from this medication's content. */
const MISSED_DOSE: ProtocolWarning = {
  id: "lupron.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    fr: "Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
  },
  sourceSegment:
    "⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
};

const WHEN_TO_CALL: ProtocolWarning = {
  id: "lupron.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Call your clinic for severe site reactions (spreading redness, warmth, or pus), or any unusual symptoms.",
    fr: "Contactez votre centre en cas de réaction sévère au point d'injection (rougeur qui s'étend, chaleur, écoulement purulent) ou de tout symptôme inhabituel.",
  },
  sourceSegment:
    "⚠ When to call - Call your clinic for severe site reactions (spreading redness, warmth, or pus), or any unusual symptoms.",
};

const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "lupron.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
};

export const LUPRON_PROTOCOL: InjectionProtocol = {
  medicationSlug: "lupron",
  brands: LUPRON_BRANDS,
  aliases: ["Leuprolide", "Leuproréline", "Leuprorelin"],
  indication: "agonist",
  sites: [
    {
      id: "abdomen",
      label: { en: "Lower abdomen", fr: "Bas du ventre" },
      copy: {
        en: "Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) with an alcohol swab and let dry completely.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre (à 2-5 cm du nombril) à l'alcool et laissez sécher complètement.",
      },
    },
    {
      id: "thigh",
      label: { en: "Upper thigh", fr: "Haut de la cuisse" },
      copy: {
        en: "Clean site - Clean the injection site on your upper thigh with an alcohol swab and let dry completely. Find the area by placing one hand below your hip and the other above your knee, and inject between them, on the outer half of your thigh.",
        fr: "Désinfecter le site - Désinfectez le haut de la cuisse à l'alcool et laissez sécher complètement. Repérez la zone en posant une main sous la hanche et l'autre au-dessus du genou, et injectez entre les deux, sur la moitié externe de la cuisse.",
      },
    },
  ],
  form: "vial",
  prescriptionForms: ["injection_solution"],
  route: "subcutaneous",
  version: "2026-09-24.1",
  derivedFrom: "content/modules/medications/gnrh-agonists.ts#leuprolide",
  steps: [
    {
      id: "lupron.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash hands thoroughly. Gather an insulin syringe (28-31G, ½ inch), alcohol swabs, and your {brand} vial. Clean the vial top with an alcohol swab.",
        fr: "Préparer - Lavez-vous soigneusement les mains. Préparez une seringue à insuline (28-31 G, 12 mm), des compresses alcoolisées et votre flacon de {brand}. Désinfectez le bouchon du flacon à l'alcool.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // Confirmed, because this is the step that can be wrong invisibly. The
      // volumes are tiny enough that the marking *is* the dose, and a micro-dose
      // protocol may have been diluted first — in which case every marking means
      // something else.
      id: "lupron.vial.draw-dose",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Draw dose - Draw your prescribed dose carefully. {brand} uses very small volumes - read the syringe markings precisely. For micro-dose protocols, your clinic may instruct dilution with bacteriostatic water before use - follow their dilution instructions exactly, as the concentration changes your dose volume.",
        fr: "Prélever la dose - Aspirez la dose prescrite avec précision. Le {brand} s'utilise à très faibles volumes - lisez précisément les graduations. Pour les protocoles micro-dose, votre centre peut demander une dilution avec de l'eau bactériostatique ; suivez ses instructions à la lettre.",
      },
    },
    {
      // Names one site, and `sites` above names both: the walk asks her which
      // before this step and swaps in the sentence she chose. The default is
      // the abdomen because that is the one the camera can outline.
      id: "lupron.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) with an alcohol swab and let dry completely.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre (à 2-5 cm du nombril) à l'alcool et laissez sécher complètement.",
      },
    },
    {
      id: "lupron.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin, insert the needle at a 90° angle, and inject slowly. For trigger use, inject the single prescribed dose 34-36 hours before retrieval.",
        fr: "Injecter - Pincez la peau, insérez l'aiguille à 90° et injectez lentement. Pour un usage comme déclencheur, injectez la dose unique prescrite 34 à 36 heures avant la ponction.",
      },
      warnings: [WHEN_TO_CALL, BREATHING_OR_ALLERGIC],
    },
    {
      id: "lupron.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Release the pinch and withdraw the needle. Do not rub the site - apply gentle pressure with gauze if needed. Rotate injection sites daily to prevent irritation. Dispose of the syringe in a sharps container.",
        fr: "Après l'injection - Relâchez le pli cutané et retirez l'aiguille. Ne frottez pas le site - appliquez si besoin une légère pression avec une compresse. Alternez les sites d'injection chaque jour pour limiter l'irritation. Jetez la seringue dans un collecteur.",
      },
    },
  ],
};
